import { cachedDetect } from '../../../../../../utilities/detect.js';
import { filter } from '../../../../../../utilities/obj.js';
import { filter as filterAssets, READY } from '../../../../../../utilities/assets.js';
import type { MediaAsset, Detect } from '../../../../../../types/player-api-types.ts';

const detect = cachedDetect() as Detect;

interface SelectableAssetsOptions {
  hdr?: boolean;
}

/**
 * Filters and sorts video assets based on container format, HDR support, and width
 * @param {MediaAsset[]} allAssets - Array of all available media assets
 * @param {SelectableAssetsOptions} options - Options object with HDR preference flag
 * @returns Filtered array of media assets
 */
export const selectableAssets = (
  allAssets: MediaAsset[],
  options: SelectableAssetsOptions = { hdr: false },
): MediaAsset[] => {
  let result = filterAssets(allAssets, {
    container: detect.video.webm ? /webm|mp4/ : 'mp4',
    sortBy: 'width asc, bitrate desc',
    public: true,
    status: READY,
  }) as MediaAsset[];

  // Filter the assets so there's only one of each size.
  // Prefer webm assets to mp4 assets since they'll be smaller than their
  // counterparts.
  result = filter(result, (asset: MediaAsset, index: number) => {
    if (asset.container === 'webm') {
      // Always prefer webm assets (i.e. never filter them out)
      return true;
    }

    // assets are sorted by width. If we support HDR, we need to check the next and previous assets to see if we actually have
    // any hdr assets. If we do, we should filter out this asset. If we don't we should keep it.
    if (detect.hdr && options.hdr) {
      const nextAsset = result[index + 1] as MediaAsset | undefined;
      const prevAsset = result[index - 1] as MediaAsset | undefined;

      const nextAssetIsHdrAtSameWidth =
        nextAsset?.type === 'hdr_video' && nextAsset.width === asset.width;
      const prevAssetIsHdrAtSameWidth =
        prevAsset?.type === 'hdr_video' && prevAsset.width === asset.width;

      if ((nextAssetIsHdrAtSameWidth || prevAssetIsHdrAtSameWidth) && asset.type !== 'hdr_video') {
        return false;
      }
    }
    // additionally, if we don't support HDR, we should filter out any hdr assets we do have
    else if (asset.type === 'hdr_video') {
      return false;
    }

    // If there isn't a webm with the same width as this asset, then keep it.
    // Otherwise, it's a duplicate, so filter it out.
    const webmDuplicates = filter(allAssets, (otherAsset: MediaAsset) => {
      return otherAsset.container === 'webm' && otherAsset.width === asset.width;
    }) as MediaAsset[];
    return webmDuplicates.length === 0;
  }) as MediaAsset[];

  return result;
};
